import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const LOCAL_KEY = "llmetric_favorites";
const FavoritesContext = createContext(null);
const keyOf = (i) => `${i.kind}:${i.ref}`;

function merge(a, b) {
  const seen = new Set(a.map(keyOf));
  return [...a, ...b.filter((i) => !seen.has(keyOf(i)))].slice(0, 200);
}

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(() => JSON.parse(localStorage.getItem(LOCAL_KEY) || "[]"));
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const savingRef = useRef(false);

  useEffect(() => {
    if (user === null) return;
    setLoaded(false);
    setLoadFailed(false);
    if (!user) {
      setItems(JSON.parse(localStorage.getItem(LOCAL_KEY) || "[]"));
      setLoaded(true);
      return;
    }
    api.get("/favorites").then(async ({ data }) => {
      const local = JSON.parse(localStorage.getItem(LOCAL_KEY) || "[]");
      let list = data.items;
      if (local.length) {
        const merged = merge(data.items, local);
        try {
          await api.put("/favorites", { items: merged });
          localStorage.removeItem(LOCAL_KEY);
          list = merged;
        } catch (e) {
          console.error("Fusion des favoris impossible", e);
          toast.error("Vos favoris de cet appareil n'ont pas pu être ajoutés au compte, ils sont conservés", { id: "fav-merge" });
        }
      }
      setItems(list);
      setLoaded(true);
    }).catch((e) => {
      console.error("Chargement des favoris impossible", e);
      toast.error("Impossible de charger vos favoris", { id: "fav-load" });
      setLoadFailed(true);
      setLoaded(true);
    });
  }, [user, reloadKey]);

  const save = useCallback(async (list, message) => {
    if (!user) {
      setItems(list);
      localStorage.setItem(LOCAL_KEY, JSON.stringify(list));
      toast.success(message);
      return;
    }
    if (savingRef.current) {
      toast("Enregistrement en cours, un instant…", { id: "fav-saving" });
      return;
    }
    savingRef.current = true;
    try {
      await api.put("/favorites", { items: list });
      setItems(list);
      toast.success(message);
    } catch (e) {
      console.error("Enregistrement du favori impossible", e);
      if (e.response?.status !== 401) toast.error("Impossible d'enregistrer le favori");
    } finally {
      savingRef.current = false;
    }
  }, [user]);

  const value = useMemo(() => {
    const isFavorite = (kind, ref) => items.some((i) => i.kind === kind && i.ref === ref);
    const ensureReady = () => {
      if (!loaded) {
        toast("Chargement de vos favoris, réessayez dans un instant", { id: "fav-wait" });
        return false;
      }
      if (loadFailed) {
        toast.error("Vos favoris n'ont pas pu être chargés, nouvel essai en cours…", { id: "fav-load" });
        setReloadKey((k) => k + 1);
        return false;
      }
      return true;
    };
    const toggle = (item) => {
      if (!ensureReady()) return;
      if (isFavorite(item.kind, item.ref)) {
        save(items.filter((i) => keyOf(i) !== keyOf(item)), "Retiré des favoris");
      } else {
        save([item, ...items].slice(0, 200), user ? "Ajouté aux favoris de votre compte" : "Ajouté aux favoris (sur cet appareil)");
      }
    };
    const addMany = (list) => {
      if (!ensureReady()) return;
      const next = merge(items, list);
      const added = next.length - items.length;
      if (!added) {
        toast("Déjà dans vos favoris");
        return;
      }
      save(next, `${added} modèle(s) ajouté(s) à vos favoris`);
    };
    return { items, loaded, isFavorite, toggle, addMany };
  }, [items, loaded, loadFailed, save, user]);

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export const useFavorites = () => useContext(FavoritesContext);
